import * as React from 'react'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'

import StatsRow from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/StatsRow'
import { isHourlyFixedBalanceAllowance } from 'modules/timeoff/components/Leaves/Overview/formatCounterAmount'
import type Allowance from 'modules/timeoff/store/models/Allowance'

import type {
  Cycle,
  ParsedAllowanceStat,
} from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/allowanceStatUtils'

import TakenTooltip from './TakenTooltip'

interface Props {
  allowance: Allowance
  selectedStat: ParsedAllowanceStat
  selectedCycle: Cycle
  nextCycle: Cycle | null | undefined
}

const TakenDays = ({ allowance, selectedStat, selectedCycle, nextCycle }: Props) => {
  const { has_dev_timeoff_new_counter_calculations } = useFeatures([
    'dev_timeoff_new_counter_calculations',
  ])
  const stat = selectedStat.toJS()

  let remainingCarryover: number
  let remainingAdjustment: number
  let remainingDays: number
  let advance: number

  if (
    has_dev_timeoff_new_counter_calculations &&
    stat.carry_over_remaining != null &&
    stat.incidences_remaining != null &&
    stat.accrued_remaining != null &&
    stat.taken_as_advance != null
  ) {
    remainingCarryover = stat.carry_over_remaining
    remainingAdjustment = stat.incidences_remaining
    remainingDays = stat.accrued_remaining
    advance = stat.taken_as_advance
  } else {
    let usedDays = stat.used_days
    const carryOver = stat.accumulated_carry_over
    const adjustment = stat.incidences
    const proratedDays = stat.prorated_allowance_days
    remainingCarryover = 0
    remainingAdjustment = 0
    remainingDays = 0

    if (carryOver > 0) {
      remainingCarryover = carryOver - usedDays

      if (remainingCarryover < 0) {
        usedDays = usedDays - carryOver
        remainingCarryover = 0
      }
    }

    if (adjustment > 0) {
      remainingAdjustment = adjustment - usedDays

      if (remainingAdjustment < 0) {
        usedDays = usedDays - adjustment
        remainingAdjustment = 0
      }
    }

    if (proratedDays > 0) {
      remainingDays = proratedDays - usedDays

      if (remainingDays < 0) {
        usedDays = usedDays - proratedDays
        remainingDays = 0
      }
    }

    advance = usedDays
  }

  const title = allowance.isDays
    ? i18n.t('time_off.taken_tooltip.taken_days')
    : i18n.t('time_off.taken_tooltip.taken_hours')

  return (
    <StatsRow
      title={title}
      amount={stat.used_days}
      isHourlyFixedBalance={isHourlyFixedBalanceAllowance(allowance)}
      tooltip={
        <TakenTooltip
          allowance={allowance}
          selectedStat={selectedStat}
          remainingCarryover={remainingCarryover}
          remainingAdjustment={remainingAdjustment}
          remainingDays={remainingDays}
          usedDays={advance}
          selectedCycle={selectedCycle}
          nextCycle={nextCycle}
        />
      }
    />
  )
}

export default TakenDays
